import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'
import { logger } from '@/lib/utils/logger'

export type EvidenceReportType =
  | 'requirements_doc'
  | 'traceability_matrix'
  | 'test_plan'
  | 'test_execution_report'
  | 'coverage_report'
  | 'compliance_report'
  | 'security_audit'
  | 'release_assessment'
  | 'risk_decision_report'
  | 'executive_summary'
  | 'audit_binder'
  | 'defect_summary'
  | 'custom'

export type EvidenceOutputFormat =
  | 'pdf'
  | 'html'
  | 'markdown'
  | 'json'
  | 'docx'
  | 'xlsx'
  | 'PDF'
  | 'HTML'
  | 'MARKDOWN'
  | 'JSON'
  | 'DOCX'
  | 'XLSX'

export type EvidenceDocumentStatus =
  | 'queued'
  | 'processing'
  | 'rendering'
  | 'uploading'
  | 'completed'
  | 'failed'
  | 'cancelled'

export interface EvidenceDocument {
  id: number
  tenant_id: number | string
  project_id: number | null
  title: string
  document_type: EvidenceReportType | string
  requested_formats: EvidenceOutputFormat[]
  status: EvidenceDocumentStatus | string
  error_message?: string | null
  input_params?: Record<string, any>
  requested_by?: number
  template_id?: number | null
  created_at: string
  updated_at: string
  completed_at?: string | null
  artifacts?: EvidenceArtifact[]
}

export interface EvidenceArtifact {
  id: number
  document_id: number
  format?: EvidenceOutputFormat | string
  output_format?: EvidenceOutputFormat | string
  file_name: string
  file_size: number
  gcs_path?: string
  content_type?: string
  sha256_checksum?: string | null
  page_count?: number | null
  rendering_duration_ms?: number | null
  created_at: string
}

export interface EvidenceDocumentsResponse {
  items?: EvidenceDocument[]
  documents?: EvidenceDocument[]
  total?: number
  limit?: number
  offset?: number
}

export interface EvidenceReportListParams {
  project_id?: number
  document_type?: EvidenceReportType | string
  status?: EvidenceDocumentStatus | string
  limit?: number
  offset?: number
}

export interface GenerateEvidenceReportRequest {
  project_id: number
  project_name?: string
  title?: string
  document_type: EvidenceReportType
  requested_formats: EvidenceOutputFormat[]
  input_params?: Record<string, any>
  template_id?: number
}

export interface DownloadResponse {
  download_url: string
  expires_in_seconds?: number
}

export const evidenceReportKeys = {
  all: ['evidence-reports'] as const,
  list: (params?: EvidenceReportListParams) => [...evidenceReportKeys.all, 'list', params] as const,
  detail: (documentId?: number | null) => [...evidenceReportKeys.all, 'detail', documentId] as const,
}

export const REPORT_TYPE_LABELS: Record<string, string> = {
  requirements_doc: 'Generated Requirements',
  traceability_matrix: 'Traceability Matrix',
  test_plan: 'Test Plan',
  test_execution_report: 'Test Execution Report',
  coverage_report: 'Coverage Report',
  compliance_report: 'Compliance Report',
  security_audit: 'Security Evidence Report',
  release_assessment: 'Release Readiness',
  risk_decision_report: 'Risk Decision Report',
  executive_summary: 'Executive Summary',
  audit_binder: 'Audit Binder',
  defect_summary: 'Defect Summary',
  custom: 'Custom Report',
}

export const REPORT_TYPE_DESCRIPTIONS: Record<string, string> = {
  requirements_doc: 'Formal SRS/DRD-style requirements document generated from canonical evidence.',
  traceability_matrix: 'Spreadsheet linking requirements, verification methods, evidence, coverage, and risk.',
  test_plan: 'Evidence-backed test planning package derived from requirements and risk signals.',
  test_execution_report: 'Execution evidence package summarizing results, failures, and supporting artifacts.',
  coverage_report: 'Coverage summary across requirements, tests, executions, and evidence.',
  compliance_report: 'Control mapping report backed by canonical evidence and compliance findings.',
  security_audit: 'Security evidence report combining CVE/security analysis and remediation posture.',
  release_assessment: 'Release readiness assessment with evidence-backed go/no-go risk posture.',
  risk_decision_report: 'Evidence-based risk decision artifact for leadership and release governance.',
  executive_summary: 'Executive-ready summary of operational evidence, risk, and readiness.',
  audit_binder: 'Comprehensive audit evidence package for review and retention.',
  defect_summary: 'Defect evidence summary linking defects to requirements, tests, and risk.',
  custom: 'Custom governed report package.',
}

export function normalizeFormat(format?: EvidenceOutputFormat | string | null): string {
  return String(format || '').trim().toLowerCase()
}

export function normalizeStatus(status?: string | null): string {
  return String(status || '').trim().toLowerCase()
}

export function getArtifactFormat(artifact: EvidenceArtifact): string {
  return normalizeFormat(artifact.format || artifact.output_format)
}

export function reportTypeLabel(type?: string | null): string {
  const key = String(type || '').trim()
  return REPORT_TYPE_LABELS[key] || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Report'
}

export function isTerminalStatus(status?: string | null): boolean {
  const normalized = normalizeStatus(status)
  return ['completed', 'failed', 'cancelled'].includes(normalized)
}

export function isActiveStatus(status?: string | null): boolean {
  const normalized = normalizeStatus(status)
  return ['queued', 'processing', 'rendering', 'uploading'].includes(normalized)
}

export function artifactMatchesFormat(artifact: EvidenceArtifact, requested: string): boolean {
  const format = getArtifactFormat(artifact)
  const contentType = String(artifact.content_type || '').toLowerCase()
  const fileName = String(artifact.file_name || '').toLowerCase()
  const normalizedRequested = normalizeFormat(requested)

  if (format === normalizedRequested) return true
  if (normalizedRequested === 'pdf') return contentType === 'application/pdf' || fileName.endsWith('.pdf')
  if (normalizedRequested === 'xlsx') {
    return (
      contentType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      fileName.endsWith('.xlsx')
    )
  }
  if (normalizedRequested === 'docx') {
    return (
      contentType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      fileName.endsWith('.docx')
    )
  }
  return fileName.endsWith(`.${normalizedRequested}`)
}

export function findBestArtifact(artifacts: EvidenceArtifact[] | undefined, preferredFormat?: string): EvidenceArtifact | undefined {
  if (!artifacts?.length) return undefined
  if (preferredFormat) {
    const match = artifacts.find(artifact => artifactMatchesFormat(artifact, preferredFormat))
    if (match) return match
  }
  return (
    artifacts.find(artifact => artifactMatchesFormat(artifact, 'pdf')) ||
    artifacts.find(artifact => artifactMatchesFormat(artifact, 'xlsx')) ||
    artifacts[0]
  )
}

function normalizeDocumentsResponse(data: EvidenceDocumentsResponse | EvidenceDocument[]): EvidenceDocument[] {
  if (Array.isArray(data)) return data
  return data.items || data.documents || []
}

async function listEvidenceReports(params?: EvidenceReportListParams): Promise<EvidenceDocument[]> {
  const { data } = await apiClient.get<EvidenceDocumentsResponse | EvidenceDocument[]>('/api/documents', { params })
  return normalizeDocumentsResponse(data)
}

async function getEvidenceReport(documentId: number): Promise<EvidenceDocument> {
  const { data } = await apiClient.get<EvidenceDocument>(`/api/documents/${documentId}`)
  return data
}

async function generateEvidenceReport(request: GenerateEvidenceReportRequest): Promise<EvidenceDocument> {
  const projectName = request.project_name || request.input_params?.metadata?.project_name || 'Selected Project'
  const reportLabel = reportTypeLabel(request.document_type)
  const generatedDate = new Date().toISOString()

  const payload = {
    project_id: request.project_id,
    title: request.title || `${projectName} - ${reportLabel}`,
    document_type: request.document_type,
    requested_formats: request.requested_formats.map(format => normalizeFormat(format)),
    template_id: request.template_id,
    input_params: {
      metadata: {
        project_name: projectName,
        title: request.title || `${projectName} - ${reportLabel}`,
        report_type: request.document_type,
        report_type_label: reportLabel,
        version: '1.0',
        author: 'QxProveIt AI Augmented',
        organization: 'Generated by QxProveIt',
        generated_date: generatedDate,
        ...(request.input_params?.metadata || {}),
      },
      project_name: projectName,
      report_type: request.document_type,
      report_type_label: reportLabel,
      generated_date: generatedDate,
      ...(request.input_params || {}),
    },
  }

  const { data } = await apiClient.post<EvidenceDocument>('/api/documents/generate', payload)
  return data
}

async function getArtifactDownloadUrl(documentId: number, artifactId: number): Promise<string> {
  const { data } = await apiClient.get<DownloadResponse>(`/api/documents/${documentId}/artifacts/${artifactId}/download`)
  return data.download_url
}

async function downloadEvidenceArtifact(params: {
  documentId: number
  artifactId?: number
  preferredFormat?: string
}): Promise<string> {
  let artifactId = params.artifactId

  if (!artifactId) {
    const document = await getEvidenceReport(params.documentId)
    const artifact = findBestArtifact(document.artifacts, params.preferredFormat)
    if (!artifact) {
      throw new Error('No downloadable artifact is available for this report yet.')
    }
    artifactId = artifact.id
  }

  return getArtifactDownloadUrl(params.documentId, artifactId)
}

export function useEvidenceReports(params?: EvidenceReportListParams) {
  return useQuery({
    queryKey: evidenceReportKeys.list(params),
    queryFn: () => listEvidenceReports(params),
    refetchInterval: (query) => {
      const docs = query.state.data || []
      return docs.some(doc => isActiveStatus(doc.status)) ? 2500 : false
    },
  })
}

export function useEvidenceReport(documentId: number | null, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: evidenceReportKeys.detail(documentId),
    queryFn: () => getEvidenceReport(documentId!),
    enabled: !!documentId && (options?.enabled ?? true),
    refetchInterval: (query) => {
      const data = query.state.data
      return data && isActiveStatus(data.status) ? 2500 : false
    },
  })
}

export function useGenerateEvidenceReport() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: generateEvidenceReport,
    onSuccess: (doc) => {
      queryClient.invalidateQueries({ queryKey: evidenceReportKeys.all })
      queryClient.invalidateQueries({ queryKey: ['documents'] })
      if (doc?.id) {
        queryClient.invalidateQueries({ queryKey: evidenceReportKeys.detail(doc.id) })
      }
    },
    onError: (error) => {
      logger.error('Failed to generate evidence report', error)
    },
  })
}

export function useDownloadEvidenceArtifact() {
  return useMutation({
    mutationFn: downloadEvidenceArtifact,
    onSuccess: (downloadUrl) => {
      window.open(downloadUrl, '_blank')
    },
    onError: (error) => {
      logger.error('Failed to download evidence artifact', error)
    },
  })
}
